import Routes from '../routes/Routes';
import { FamlaServices } from '../services';

import { markInterviewDeferredMappingOpen } from './mappingLoadingOverlay';

export type LocalSheetRef = {
    _id?: string;
    id?: string;
    swimlane?: string;
    line?: string;
    second?: string;
    is_finish?: boolean;
    name?: string;
    process?: {
        _id?: string;
        owner?: Record<string, unknown>;
        [key: string]: unknown;
    };
};

/** État React Router passé à ProcessDiagram pour éviter un GET crédits / liste au clic. */
export type MappingNavigationState = {
    prefetchedSheets?: LocalSheetRef[];
    knownAccessible?: boolean;
    /** Création canva vierge (MANUAL) — affichage direct sans écran noir. */
    blankCanvasOpen?: boolean;
    /** Ouverture depuis interview — garder écran noir malgré prefetchedSheets. */
    skipPrefetchedInstantOpen?: boolean;
};

export type MappingAccessDenied = {
    creditsRequired: number;
    creditsAvailable: number;
    deficit: number;
    plan?: string;
};

export type MappingAccessResult =
    | { allowed: true }
    | { allowed: false; denied: MappingAccessDenied };

export type NavigateToMappingResult =
    | { success: true }
    | { success: false; denied: MappingAccessDenied };

export function buildMappingPath(processId: string, mapId: string, analysisId?: string): string {
    const base = `${Routes.MAPPING}${processId}/${mapId}`;
    return analysisId ? `${base}/${analysisId}` : base;
}

/** Parse `/process/mapping/{processId}/{mapId}/{analysisId?}` */
export function parseMappingRoute(route: string): {
    processId: string;
    mapId: string;
    analysisId?: string;
} | null {
    const prefix = Routes.MAPPING;
    if (!route.startsWith(prefix)) return null;
    const parts = route.slice(prefix.length).split('/').filter(Boolean);
    if (parts.length < 2) return null;
    return {
        processId: parts[0],
        mapId: parts[1],
        analysisId: parts[2],
    };
}

/**
 * Vérifie si une cartographie peut être ouverte (pay-to-view).
 * Ne court-circuite pas via cache local : seul l'appelant (knownAccessible) peut skip.
 */
export async function checkMappingOpenAccess(
    processId: string,
    mapId: string,
    accessToken: string,
): Promise<MappingAccessResult> {
    if (!processId || !mapId || !accessToken) {
        return { allowed: true };
    }

    try {
        const data = await FamlaServices(
            'api_host',
            `/mapping/process/${processId}`,
            'GET',
            '',
            accessToken,
        );

        if (data.status === 200) {
            return { allowed: true };
        }

        if (data.status === 402) {
            const response = data.body?.data;
            const searchId = String(mapId);
            const accessibleMapping = response?.accessible?.find((m: { _id?: string; id?: string }) => {
                const mappingId = String(m._id || m.id || '');
                return mappingId === searchId;
            });

            if (accessibleMapping) {
                return { allowed: true };
            }

            if (response?.locked) {
                return {
                    allowed: false,
                    denied: {
                        creditsRequired: response.locked.credits_required,
                        creditsAvailable: response.locked.credits_available,
                        deficit: response.locked.deficit,
                        plan: response.locked.plan || 'FREE',
                    },
                };
            }
        }

        return { allowed: true };
    } catch {
        return { allowed: true };
    }
}

/** Convertit un mapping API en feuille locale prête pour navigation immédiate. */
export function toLocalSheetRefFromMapping(
    data: Record<string, unknown> | null | undefined,
): LocalSheetRef | null {
    if (!data) return null;
    const mapId = String(data._id ?? data.id ?? '');
    if (!mapId) return null;
    const swimlane = typeof data.swimlane === 'string' ? data.swimlane : undefined;
    const line = typeof data.line === 'string' ? data.line : undefined;
    if (!swimlane || !line) return null;
    const process =
        data.process && typeof data.process === 'object'
            ? (data.process as LocalSheetRef['process'])
            : undefined;
    return {
        _id: mapId,
        id: mapId,
        swimlane,
        line,
        second: typeof data.second === 'string' ? data.second : undefined,
        is_finish: data.is_finish !== false,
        name: typeof data.name === 'string' ? data.name : undefined,
        process,
    };
}

function parseProcessMappingList(responseData: unknown): Record<string, unknown>[] {
    if (Array.isArray(responseData)) {
        return responseData as Record<string, unknown>[];
    }
    if (responseData && typeof responseData === 'object') {
        const accessible = (responseData as { accessible?: unknown }).accessible;
        if (Array.isArray(accessible)) {
            return accessible as Record<string, unknown>[];
        }
    }
    return [];
}

/** Liste des feuilles éditeur (swimlane + line) pour un processus. */
export async function fetchProcessEditorSheets(
    processId: string,
    accessToken: string,
): Promise<LocalSheetRef[]> {
    if (!processId || !accessToken) return [];
    try {
        const data = await FamlaServices(
            'api_host',
            `/mapping/process/${processId}?fields=editor&skip_enrich=true`,
            'GET',
            '',
            accessToken,
        );
        if (data.status !== 200 && data.status !== 402) return [];
        return parseProcessMappingList(data.body?.data)
            .map((mapping) => toLocalSheetRefFromMapping(mapping))
            .filter((sheet): sheet is LocalSheetRef => sheet !== null);
    } catch {
        return [];
    }
}

/**
 * Ouvre la dernière cartographie d'un processus MANUAL (accueil, bibliothèque).
 * Précharge les feuilles pour éviter l'écran noir à l'ouverture.
 */
export async function navigateToLatestManualMapping(params: {
    processId: string;
    accessToken: string;
    navigate?: (path: string, options?: { state?: MappingNavigationState }) => void;
}): Promise<NavigateToMappingResult> {
    try {
        const data = await FamlaServices(
            'api_host',
            `/mapping/process/${params.processId}?fields=editor&skip_enrich=true`,
            'GET',
            '',
            params.accessToken,
        );
        const rawMappings = parseProcessMappingList(data.body?.data);
        const localSheets = rawMappings
            .map((mapping) => toLocalSheetRefFromMapping(mapping))
            .filter((sheet): sheet is LocalSheetRef => sheet !== null);
        const latestRaw = rawMappings[rawMappings.length - 1];
        const latestSheet = localSheets[localSheets.length - 1];
        const mapId = String(
            latestSheet?._id ??
                latestSheet?.id ??
                latestRaw?._id ??
                latestRaw?.id ??
                '',
        );
        if ((data.status !== 200 && data.status !== 402) || !mapId) {
            return {
                success: false,
                denied: {
                    creditsRequired: 0,
                    creditsAvailable: 0,
                    deficit: 0,
                },
            };
        }

        if (data.status === 402) {
            const access = await checkMappingOpenAccess(
                params.processId,
                mapId,
                params.accessToken,
            );
            if (!access.allowed) {
                return { success: false, denied: access.denied };
            }
        }

        return navigateToMapping({
            processId: params.processId,
            mapId,
            accessToken: params.accessToken,
            localSheets: localSheets.length ? localSheets : undefined,
            knownAccessible: true,
            navigate: params.navigate,
        });
    } catch {
        return {
            success: false,
            denied: {
                creditsRequired: 0,
                creditsAvailable: 0,
                deficit: 0,
            },
        };
    }
}

/** Ouvre une cartographie après contrôle crédits (navigation ou nouvel onglet). */
export async function navigateToMapping(params: {
    processId: string;
    mapId: string;
    analysisId?: string;
    accessToken: string;
    localSheets?: LocalSheetRef[];
    /** Accès déjà validé (ex. liste interview accessible[]) — navigation immédiate. */
    knownAccessible?: boolean;
    /** Canva vierge créé à l'instant — affichage direct sans écran noir. */
    blankCanvasOpen?: boolean;
    /** Interview : conserver l'écran de chargement jusqu'au rendu. */
    skipPrefetchedInstantOpen?: boolean;
    navigationState?: MappingNavigationState;
    navigate?: (path: string, options?: { state?: MappingNavigationState }) => void;
    newTab?: boolean;
}): Promise<NavigateToMappingResult> {
    if (!params.knownAccessible) {
        const access = await checkMappingOpenAccess(
            params.processId,
            params.mapId,
            params.accessToken,
        );
        if (!access.allowed) {
            return { success: false, denied: access.denied };
        }
    }

    const path = buildMappingPath(params.processId, params.mapId, params.analysisId);
    const hasNavState =
        Boolean(params.localSheets?.length) ||
        params.knownAccessible === true ||
        params.blankCanvasOpen === true ||
        Boolean(params.navigationState?.blankCanvasOpen) ||
        Boolean(params.navigationState);
    const state: MappingNavigationState | undefined = hasNavState
        ? {
              prefetchedSheets: params.localSheets ?? params.navigationState?.prefetchedSheets,
              blankCanvasOpen:
                  params.blankCanvasOpen === true ||
                  params.navigationState?.blankCanvasOpen === true,
              skipPrefetchedInstantOpen:
                  params.skipPrefetchedInstantOpen === true ||
                  params.navigationState?.skipPrefetchedInstantOpen === true,
              // Accès validé au clic (knownAccessible ou checkMappingOpenAccess réussi).
              knownAccessible: true,
          }
        : { knownAccessible: true };

    if (params.newTab) {
        window.open(path, '_blank', 'noopener,noreferrer');
    } else if (params.navigate) {
        if (
            params.skipPrefetchedInstantOpen === true ||
            params.navigationState?.skipPrefetchedInstantOpen === true
        ) {
            markInterviewDeferredMappingOpen(params.mapId);
        }
        params.navigate(path, { state });
    } else {
        window.location.assign(path);
    }

    return { success: true };
}
